import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useLocaleNavigate } from "@/hooks/useLocaleNavigate";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import Header from "@/components/layout/Header";
import SEOHead from "@/components/seo/SEOHead";
import PaymentSetup from "@/components/dashboard/PaymentSetup";
import BillingModelSwitch from "@/components/dashboard/BillingModelSwitch";
import { useAuth } from "@/hooks/useAuth";
import { useProvider } from "@/hooks/useProvider";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  CreditCard,
  Package,
  AlertTriangle,
  Check,
  ArrowRight,
  Loader2,
  XCircle,
  ExternalLink,
  ShoppingCart,
  ArrowLeftRight,
} from "lucide-react";

type Tab = "plan" | "quotas" | "payment" | "invoices" | "model";

async function callSubscriptionApi(accessToken: string, action: string, params?: any) {
  const res = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/subscription-api`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ action, ...params }),
    }
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Anfrage fehlgeschlagen");
  return data;
}

const StatusBadge = ({ status, t }: { status: string; t: (k: string) => string }) => {
  const styles: Record<string, string> = {
    active: "bg-accent/10 text-accent border-accent/30",
    trial: "bg-primary/10 text-primary border-primary/30",
    past_due: "bg-destructive/10 text-destructive border-destructive/30",
    blocked: "bg-destructive/10 text-destructive border-destructive/30",
    canceled: "bg-muted text-muted-foreground border-border",
    pending: "bg-muted text-muted-foreground border-border",
  };
  const labelKeys: Record<string, string> = {
    active: "billing.statusActive",
    trial: "billing.statusTrial",
    past_due: "billing.statusPastDue",
    blocked: "billing.statusBlocked",
    canceled: "billing.statusCanceled",
    pending: "billing.statusPending",
  };

  return (
    <span className={`text-xs px-3 py-1 border ${styles[status] || styles.pending}`}>
      {t(labelKeys[status] || "billing.statusPending")}
    </span>
  );
};

const ProviderBilling = () => {
  const { t } = useTranslation();
  const navigate = useLocaleNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const { profile, isLoadingProfile } = useProvider();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<Tab>("plan");

  const { data: subData, isLoading: subLoading } = useQuery({
    queryKey: ["provider-subscription", profile?.id],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error(t("profileEdit.notLoggedIn"));
      return callSubscriptionApi(session.access_token, "get_subscription");
    },
    enabled: !!profile?.id,
    refetchInterval: 15_000,
  });

  const checkoutMutation = useMutation({
    mutationFn: async (planKey: string) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error(t("profileEdit.notLoggedIn"));
      const returnUrl = `${window.location.origin}/provider/billing?checkout=return`;
      return callSubscriptionApi(session.access_token, "checkout", { plan_key: planKey, return_url: returnUrl });
    },
    onSuccess: (data) => {
      window.location.href = data.checkout_url;
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const addonCheckoutMutation = useMutation({
    mutationFn: async ({ addonKey, qty }: { addonKey: string; qty: number }) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error(t("profileEdit.notLoggedIn"));
      const returnUrl = `${window.location.origin}/provider/billing?addon=return`;
      return callSubscriptionApi(session.access_token, "addon_checkout", {
        addon_key: addonKey, qty, return_url: returnUrl,
      });
    },
    onSuccess: (data) => {
      window.location.href = data.checkout_url;
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const cancelMutation = useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error(t("profileEdit.notLoggedIn"));
      return callSubscriptionApi(session.access_token, "cancel");
    },
    onSuccess: () => {
      toast.success(t("billing.cancelSuccess"));
      queryClient.invalidateQueries({ queryKey: ["provider-subscription"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (authLoading || isLoadingProfile || subLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  if (!user) {
    navigate("/auth");
    return null;
  }

  const sub = subData?.subscription;
  const plans = subData?.plans || [];
  const addons = subData?.addons || [];
  const plan = sub?.plan;
  const status = sub?.status || "none";
  const isPastDue = status === "past_due";
  const isBlocked = status === "blocked";

  const tabs: { key: Tab; label: string; icon: any; href?: string }[] = [
    { key: "model", label: "Abrechnungsmodell", icon: ArrowLeftRight },
    { key: "plan", label: t("billing.tabPlan"), icon: Package },
    { key: "quotas", label: t("billing.tabQuotas"), icon: Check },
    { key: "payment", label: t("billing.tabPayment"), icon: CreditCard },
    { key: "invoices", label: t("invoices.title"), icon: CreditCard, href: "/provider/invoices" },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead title={t("billing.title")} noindex canonical="/provider/billing" />
      <Header />

      <main className="pt-24 pb-12">
        {/* Banners */}
        {isPastDue && (
          <div className="section-padding py-3 bg-destructive/10 border-b border-destructive/30">
            <div className="max-w-5xl mx-auto flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-destructive shrink-0" />
              <p className="text-sm text-destructive font-medium">{t("billing.pastDueBanner")}</p>
              <Button variant="quiet" size="sm" onClick={() => setActiveTab("payment")} className="ml-auto">
                {t("billing.updatePayment")}
              </Button>
            </div>
          </div>
        )}
        {isBlocked && (
          <div className="section-padding py-3 bg-destructive/10 border-b border-destructive/30">
            <div className="max-w-5xl mx-auto flex items-center gap-3">
              <XCircle className="w-5 h-5 text-destructive shrink-0" />
              <p className="text-sm text-destructive font-medium">{t("billing.blockedBanner")}</p>
            </div>
          </div>
        )}

        <div className="section-padding border-b border-border">
          <div className="max-w-5xl mx-auto py-6">
            <h1 className="text-headline">{t("billing.title")}</h1>
            <p className="text-caption">{t("billing.subtitle")}</p>
          </div>
        </div>

        <div className="section-padding">
          <div className="max-w-5xl mx-auto mt-8">
            {/* Tab Navigation */}
            <div className="flex gap-1 border-b border-border mb-8 overflow-x-auto">
              {tabs.map((tab) =>
                tab.href ? (
                  <Link
                    key={tab.key}
                    to={tab.href}
                    className="flex items-center gap-2 px-4 py-3 text-sm border-b-2 border-transparent text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                  </Link>
                ) : (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm border-b-2 transition-colors whitespace-nowrap ${
                      activeTab === tab.key
                        ? "border-accent text-foreground font-medium"
                        : "border-transparent text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                )
              )}
            </div>

            {/* Plan Tab */}
            {activeTab === "plan" && (
              <div className="space-y-8">
                {sub ? (
                  <div className="p-6 md:p-8 border border-border bg-card space-y-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <h2 className="text-subheadline">{plan?.name || "—"}-Plan</h2>
                        <p className="text-caption mt-1">
                          {plan ? `${(plan.monthly_price_cents / 100).toFixed(0)} € ${t("common.perMonth")}` : ""}
                        </p>
                      </div>
                      <StatusBadge status={status} t={t} />
                    </div>
                    {sub.canceled_at_period_end && (
                      <div className="p-3 bg-muted border border-border text-sm">
                        {t("billing.cancelledNotice", { date: new Date(sub.current_period_end).toLocaleDateString("de-DE") })}
                      </div>
                    )}
                    <div className="divider-line" />
                    <div className="flex flex-wrap gap-3">
                      {!sub.canceled_at_period_end && (
                        <Button
                          variant="quiet"
                          size="sm"
                          onClick={() => {
                            if (confirm(t("billing.cancelConfirm"))) {
                              cancelMutation.mutate();
                            }
                          }}
                          disabled={cancelMutation.isPending}
                        >
                          {t("billing.cancelSub")}
                        </Button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 space-y-4">
                    <Package className="w-12 h-12 text-muted-foreground mx-auto" />
                    <h2 className="text-headline">{t("billing.noPlan")}</h2>
                    <p className="text-caption max-w-md mx-auto">{t("billing.noPlanDesc")}</p>
                  </div>
                )}

                {/* Plan Selection */}
                <div>
                  <h3 className="text-subheadline mb-6">
                    {sub ? t("billing.changePlan") : t("billing.selectPlan")}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {plans.map((p: any) => {
                      const isCurrent = plan?.id === p.id;
                      const features = Array.isArray(p.features) ? p.features : [];
                      return (
                        <div
                          key={p.id}
                          className={`p-5 border bg-card flex flex-col ${
                            isCurrent ? "border-accent" : "border-border"
                          }`}
                        >
                          <div className="flex-1 space-y-3">
                            <div className="flex items-center justify-between">
                              <h4 className="text-body font-medium">{p.name}</h4>
                              {isCurrent && (
                                <span className="text-xs px-2 py-0.5 bg-accent/10 text-accent border border-accent/30">
                                  {t("billing.currentPlan")}
                                </span>
                              )}
                            </div>
                            <p className="price-display text-2xl">
                              {(p.monthly_price_cents / 100).toFixed(0)} €
                              <span className="text-caption text-sm ml-1">{t("common.perMonth")}</span>
                            </p>
                            <p className="text-caption text-sm">
                              {p.included_accepted_leads_per_month} {t("billing.leadsIncl")}
                            </p>
                            <ul className="space-y-1.5 mt-3">
                              {features.slice(0, 4).map((f: string, fi: number) => (
                                <li key={fi} className="flex items-start gap-2 text-xs text-muted-foreground">
                                  <Check className="w-3 h-3 text-accent shrink-0 mt-0.5" />
                                  {f}
                                </li>
                              ))}
                            </ul>
                          </div>
                          <Button
                            variant={isCurrent ? "quiet" : "commit"}
                            size="sm"
                            className="w-full mt-4"
                            disabled={isCurrent || checkoutMutation.isPending}
                            onClick={() => checkoutMutation.mutate(p.key)}
                          >
                            {checkoutMutation.isPending ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : isCurrent ? (
                              t("billing.activePlan")
                            ) : (
                              <>
                                {t("billing.selectPlan")}
                                <ExternalLink className="w-3 h-3 ml-1" />
                              </>
                            )}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Addons */}
                {addons.length > 0 && sub && ["active", "trial"].includes(status) && (
                  <div>
                    <h3 className="text-subheadline mb-6">{t("billing.addonsTitle")}</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {addons.map((addon: any) => (
                        <div key={addon.id} className="p-5 border border-border bg-card flex items-center justify-between">
                          <div>
                            <h4 className="text-body font-medium">{addon.name}</h4>
                            <p className="price-display text-xl mt-1">
                              {(addon.price_cents / 100).toFixed(0)} €
                            </p>
                          </div>
                          <Button
                            variant="quiet"
                            size="sm"
                            onClick={() => addonCheckoutMutation.mutate({ addonKey: addon.key, qty: 1 })}
                            disabled={addonCheckoutMutation.isPending}
                          >
                            <ShoppingCart className="w-4 h-4 mr-1" />
                            {t("billing.buy")}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Quotas Tab */}
            {activeTab === "quotas" && (
              <div className="space-y-8">
                {sub && plan ? (
                  <>
                    <div className="p-6 md:p-8 border border-border bg-card space-y-6">
                      <h3 className="text-subheadline">{t("billing.acceptedLeads")}</h3>
                      <div className="space-y-3">
                        <div className="flex justify-between text-sm">
                          <span>
                            {sub.accepted_leads_used_this_period} / {sub.total_accepted_limit} {t("common.used")}
                          </span>
                          <span className="text-muted-foreground">
                            {Math.max(0, sub.total_accepted_limit - sub.accepted_leads_used_this_period)} {t("common.available")}
                          </span>
                        </div>
                        <Progress
                          value={Math.min(100, (sub.accepted_leads_used_this_period / sub.total_accepted_limit) * 100)}
                          className="h-3"
                        />
                        {sub.accepted_leads_used_this_period >= sub.total_accepted_limit && (
                          <p className="text-sm text-destructive font-medium flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4" />
                            {t("billing.quotaReached")}
                          </p>
                        )}
                      </div>
                    </div>

                    {plan.delivery_cap_per_month && (
                      <div className="p-6 md:p-8 border border-border bg-card space-y-6">
                        <h3 className="text-subheadline">{t("billing.deliveries")}</h3>
                        <div className="space-y-3">
                          <div className="flex justify-between text-sm">
                            <span>
                              {sub.delivered_leads_used_this_period} / {plan.delivery_cap_per_month} {t("common.used")}
                            </span>
                            <span className="text-muted-foreground">
                              {Math.max(0, plan.delivery_cap_per_month - sub.delivered_leads_used_this_period)} {t("common.available")}
                            </span>
                          </div>
                          <Progress
                            value={Math.min(100, (sub.delivered_leads_used_this_period / plan.delivery_cap_per_month) * 100)}
                            className="h-3"
                          />
                        </div>
                      </div>
                    )}

                    <div className="p-4 bg-secondary/30 border border-border text-sm space-y-1">
                      <p className="font-medium">{t("billing.billingPeriod")}</p>
                      <p className="text-muted-foreground">
                        {new Date(sub.current_period_start).toLocaleDateString("de-DE")} –{" "}
                        {new Date(sub.current_period_end).toLocaleDateString("de-DE")}
                      </p>
                      <p className="text-muted-foreground">{t("billing.periodResets")}</p>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-caption">{t("billing.noPlanDesc")}</p>
                    <Button variant="commit" className="mt-4" onClick={() => setActiveTab("plan")}>
                      {t("billing.selectPlan")}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Payment Tab */}
            {activeTab === "payment" && <PaymentSetup />}

            {/* Model Tab */}
            {activeTab === "model" && <BillingModelSwitch />}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProviderBilling;